import React, { useState, useEffect, useRef } from 'react';
// Import the JSON-based configuration
import appConfig from '../config/appConfig';

const CreateStatusDialog = ({
  isOpen,
  onClose,
  onSubmit,
  selectedEntity,
  isEditMode,
  statusToEdit,
  permissions,
  loginToken,
  corporateAccount,
  currentProject
}) => {
  const [statusCode, setStatusCode] = useState('');
  const [error, setError] = useState('');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [availableIcons, setAvailableIcons] = useState([]);
  const [selectedIconSeq, setSelectedIconSeq] = useState(null);
  const [isFetchingIcons, setIsFetchingIcons] = useState(false);

  const statusCodeInputRef = useRef(null);

  // Updated dialog styles with fixed layout (matching CreateUserDialog)
  const dialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    dialog: {
      backgroundColor: 'white',
      borderRadius: '8px',
      width: '400px',
      maxWidth: '90vw',
      height: 'auto',
      maxHeight: '90vh',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    header: {
      backgroundColor: '#CCCCFF',
      textAlign: 'center',
      padding: '10px 20px',
      borderRadius: '8px 8px 0 0',
      fontSize: '18px',
      fontWeight: 'bold',
      flexShrink: 0
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      maxHeight: 'calc(90vh - 120px)'
    },
    footer: {
      padding: '15px 20px',
      borderTop: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      borderRadius: '0 0 8px 8px',
      flexShrink: 0
    }
  };

  // Fetch available icons when dialog opens
 // Fetch available icons when dialog opens
useEffect(() => {
  const fetchStatusDetails = async () => {
    if (!isOpen || !selectedEntity) return;

    setIsFetchingIcons(true);
    setAvailableIcons([]);
    setSelectedIconSeq(null);

    try {
      const endpoint = appConfig.getUrl('/get_status_details');
      const requestBody = {
        corporate_account: corporateAccount,
        project_id: currentProject,
        entity: selectedEntity
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${loginToken}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.status === 'Success' && data.entity_details && data.entity_details.length > 0) {
        setAvailableIcons(data.entity_details);

        // If editing and status has an icon, pre-select it
        if (isEditMode && statusToEdit) {
          // Check if icon_url_seq exists and is not null
          if (statusToEdit.icon_url_seq !== null && statusToEdit.icon_url_seq !== undefined) {
            setSelectedIconSeq(statusToEdit.icon_url_seq);
          } else {
            // Explicitly set to null if no icon is assigned
            setSelectedIconSeq(null);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching status details:', err);
    } finally {
      setIsFetchingIcons(false);
    }
  };

  fetchStatusDetails();
}, [isOpen, selectedEntity, corporateAccount, currentProject, loginToken, isEditMode, statusToEdit]);


  // Focus the status code field whenever the dialog opens or after successful creation
  useEffect(() => {
    if (isOpen && statusCodeInputRef.current) {
      setTimeout(() => {
        statusCodeInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  // Reset form and focus when dialog opens
  useEffect(() => {
    if (isOpen) {
      setShowValidationErrors(false);
      setError('');

      if (isEditMode && statusToEdit) {
        setStatusCode(statusToEdit.status || '');
      } else {
        // Reset form for new status creation
        setStatusCode('');
        setSelectedIconSeq(null);
      }
    }
  }, [isOpen, isEditMode, statusToEdit]);

  const resetFormAfterCreate = () => {
    setStatusCode('');
    setSelectedIconSeq(null);
    setShowValidationErrors(false);
    setError('');

    // Focus the status code field again for next status creation
    setTimeout(() => {
      if (statusCodeInputRef.current) {
        statusCodeInputRef.current.focus();
      }
    }, 100);
  };





  const handleSubmit = async (e) => {
  if (e) {
    e.preventDefault();
  }

  setShowValidationErrors(true);
  setError('');
  setIsLoading(true);

  if (!selectedEntity) {
    setError('Please select an entity before creating a status');
    setIsLoading(false);
    return { success: false };
  }

  if (!statusCode.trim()) {
    setError('Status Code is required');
    setIsLoading(false);
    return { success: false };
  }

  const endpoint = isEditMode ?
    appConfig.getUrl('/update_status'):
    appConfig.getUrl('/create_status');

  // Prepare request body
  const requestBody = {
    corporate_account: corporateAccount,
    project_id: currentProject,
    entity: selectedEntity
  };

  if (isEditMode && statusToEdit) {
    // For UPDATE - use the API format you specified
    requestBody.old_status = statusToEdit.status;
    requestBody.new_status = statusCode.trim();

    // Add icon_url_seq if an icon was selected
    if (selectedIconSeq !== null) {
      requestBody.icon_url_seq = selectedIconSeq;
    }
  } else {
    // For CREATE - keep existing format
    requestBody.status = statusCode.trim();

    // Add icon_url_seq if an icon was selected
    if (selectedIconSeq !== null) {
      requestBody.icon_url_seq = selectedIconSeq;
    }
  }

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${loginToken}`
      },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();

    if (data.status === 'Success') {
      setError(`Status code ${isEditMode ? 'updated' : 'created'} successfully`);

      onSubmit();

      setTimeout(() => {
        if (isEditMode) {
          onClose();
        } else {
          resetFormAfterCreate();
        }
      }, 1500);

      setIsLoading(false);
      return {
        success: true,
        isEditMode: isEditMode,
        statusCode: statusCode.trim()
      };
    } else {
      throw new Error(data.status_description || 'Failed to save status code');
    }
  } catch (err) {
    setError(err.message);
    setIsLoading(false);
    return { success: false };
  }
};







  if (!isOpen) return null;

  const getEntityDisplayName = () => {
    return selectedEntity ? selectedEntity.replace(/_/g, ' ') : '';
  };

  return (
    <div style={dialogStyles.overlay}>
      <div style={dialogStyles.dialog}>
        {/* Fixed Header */}
        <div style={dialogStyles.header}>
          {isEditMode ? `Edit Status Code (${statusToEdit?.status})` : `Create New Status Code`}
        </div>

        {/* Scrollable Content */}
        <div style={dialogStyles.content}>
          <form onSubmit={handleSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1 }}>
              {/* Entity Display */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Entity:
                </label>
                <div style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#f9fafb',
                  fontSize: '14px',
                  color: '#374151'
                }}>
                  {getEntityDisplayName()} ({selectedEntity})
                </div>
              </div>

              {/* Status Code */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Status Code: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  ref={statusCodeInputRef}
                  type="text"
                  value={statusCode}
                  onChange={(e) => setStatusCode(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: `1px solid ${showValidationErrors && !statusCode.trim() ? '#dc2626' : '#ddd'}`,
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="Enter status code"
                  required
                />
                {showValidationErrors && !statusCode.trim() && (
                  <span style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Status Code is required
                  </span>
                )}
              </div>

              {/* Icon Selection - only show if icons are available */}
              {isFetchingIcons && (
                <div style={{ marginBottom: '15px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                  Loading icon options...
                </div>
              )}

              {!isFetchingIcons && availableIcons.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Select Icon (Optional):
                  </label>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
                    gap: '8px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#f9fafb'
                  }}>
                    {/* None option */}
                    <div
                      onClick={() => setSelectedIconSeq(null)}
                      style={{
                        width: '60px',
                        height: '60px',
                        border: selectedIconSeq === null ? '2px solid #3b82f6' : '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: selectedIconSeq === null ? '#eff6ff' : 'white',
                        fontSize: '10px',
                        textAlign: 'center',
                        color: '#6b7280'
                      }}
                    >
                      None
                    </div>

                    {/* Icon options */}
                    {availableIcons.map((icon) => (
                      <div
                        key={icon.seq_no}
                        onClick={() => setSelectedIconSeq(icon.seq_no)}
                        style={{
                          width: '60px',
                          height: '60px',
                          border: selectedIconSeq === icon.seq_no ? '2px solid #3b82f6' : '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: selectedIconSeq === icon.seq_no ? '#eff6ff' : 'white',
                          padding: '4px',
                          transition: 'all 0.2s'
                        }}
                        title={`Icon ${icon.seq_no}`}
                      >
                        <img
                          src={icon.icon_url}
                          alt={`Icon ${icon.seq_no}`}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Fixed Footer */}
        <div style={dialogStyles.footer}>
          {/* Error message */}
          {error && (
            <div style={{
              marginBottom: '15px',
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: error.includes('successfully') ? '#f0f9ff' : '#fef2f2',
              color: error.includes('successfully') ? '#059669' : '#dc2626',
              border: error.includes('successfully') ? '1px solid #10b981' : '1px solid #ef4444'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              disabled={isLoading}
            >
              Close
            </button>

            <button
              type="submit"
              onClick={(e) => {
                if (!permissions.canUpdate) {
                  e.preventDefault();
                  setError('You do not have permission to create or edit status codes');
                  return;
                }
                handleSubmit(e);
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: !permissions.canUpdate ? '#f3f4f6' : '#C8F4DE',
                color: !permissions.canUpdate ? '#6b7280' : 'black',
                border: !permissions.canUpdate ? '1px solid #d1d5db' : 'none',
                borderRadius: '4px',
                fontSize: '14px',
                opacity: isLoading ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                cursor: !permissions.canUpdate || isLoading ? 'not-allowed' : 'pointer'
              }}
              disabled={isLoading || !permissions.canUpdate}
            >
              {isLoading && (
                <svg style={{
                  animation: 'spin 1s linear infinite',
                  marginLeft: '-4px',
                  marginRight: '8px',
                  width: '16px',
                  height: '16px'
                }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {!permissions.canUpdate ?
                (isEditMode ? 'No Permission to Update' : 'No Permission to Create') :
                (isLoading ? 'Processing...' : (isEditMode ? 'Update Status' : 'Create Status'))
              }
            </button>
          </div>
        </div>
      </div>

      {/* Add CSS for spin animation */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CreateStatusDialog;